import { LeafletDirective, LeafletLayersDirective } from "@bluehalo/ngx-leaflet";
import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import {
  Circle,
  FitBoundsOptions,
  LatLngBounds,
  Layer,
  Map as LeafletMap,
  MapOptions,
  Marker,
  circle,
  divIcon,
  latLng,
  latLngBounds,
  marker,
  tileLayer,
} from "leaflet";

import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  EnvironmentInjector,
  NgZone,
  computed,
  createComponent,
  inject,
  input,
  model,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";

import { HealthStatus, IotnetGateway } from "../../../apis/stats/rest/models";
import { ENV } from "../../../app/app.runtime";
import { IotnetGatewayCaption } from "../iotnet-gateway-caption/iotnet-gateway-caption";
import { IotnetGatewayElement } from "../iotnet-gateway-element/iotnet-gateway-element";
import { IotnetMapFilter } from "../iotnet-gateway-filter/iotnet-gateway-filter";
import { hasCoordinates } from "../iotnet-gateway-helpers";
import { gatewayTags, isIotnetGateway } from "../iotnet-gateway-tags";
import { IotnetMapInfo } from "../iotnet-map-info/iotnet-map-info";

const DEFAULT_BOUNDS = latLngBounds(latLng(50.75, 3.2), latLng(53.7, 7.22)); // Bounds of the Netherlands
const MARKER_SIZE = 34;

const GATEWAY_RANGE_METERS = 2000;
const GATEWAY_RANGE_COLOR = "#177abf"; // $SurfBlue100

// Matches $breakpoint-s-2: below it the expanded gateway list covers most of
// the map, so it starts collapsed and closes when a gateway is selected.
const NARROW_SCREEN_QUERY = "(max-width: 840px)";

function isNarrowScreen(): boolean {
  // Optional call: jsdom (Jest) does not implement matchMedia.
  return window.matchMedia?.(NARROW_SCREEN_QUERY).matches ?? false;
}

@Component({
  selector: "iotnet-map",
  templateUrl: "iotnet-map.html",
  styleUrls: ["./iotnet-map.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    FormsModule,
    LeafletDirective,
    LeafletLayersDirective,
    IotnetGatewayElement,
    IotnetMapFilter,
    IotnetMapInfo,
    TranslatePipe,
  ],
})
export class IotnetMap {
  private readonly zone = inject(NgZone);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  public readonly gateways = input<IotnetGateway[]>([]);
  public readonly selectedGateway = model<IotnetGateway | null>(null);
  protected readonly gatewaySearch = signal("");
  protected readonly listExpanded = signal(!isNarrowScreen());

  protected readonly activeStatuses = signal<HealthStatus[]>([]);
  protected readonly activeTags = signal<string[]>([]);
  protected readonly availableTags = computed(() => [
    ...new Set(this.gateways().flatMap((gateway) => gatewayTags(gateway))),
  ]);

  protected readonly filteredGateways = computed(() => {
    const query = this.gatewaySearch().trim().toLowerCase();
    const statuses = this.activeStatuses();
    const tags = this.activeTags();

    return this.gateways()
      .filter((gateway) => statuses.length === 0 || statuses.includes(gateway.health))
      .filter((gateway) => tags.length === 0 || gatewayTags(gateway).some((tag) => tags.includes(tag)))
      .filter(
        (gateway) =>
          query === ""
          || [gateway.gateway_name, gateway.gateway_id, gateway.tenant_name]
            .map((field) => field.toLowerCase())
            .some((field) => field.includes(query)),
      );
  });
  private readonly mappableGateways = computed(() => this.gateways().filter(hasCoordinates));

  protected readonly options: MapOptions = {
    layers: [
      tileLayer(ENV.MAP_TILE_URL, {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        referrerPolicy: "strict-origin-when-cross-origin",
      }),
    ],
  };

  protected readonly fitBoundsOptions: FitBoundsOptions = { padding: [24, 24], maxZoom: 15 };
  private readonly map = signal<LeafletMap | null>(null);
  private static readonly GATEWAY_ZOOM = 15;

  protected readonly markers = computed<Layer[]>(() => {
    const gateways = this.filteredGateways().filter(hasCoordinates);
    const circles = gateways
      .filter((gateway) => isIotnetGateway(gateway) && gateway.health === HealthStatus.Ok)
      .map((gateway) => this.toRangeCircle(gateway));
    return [...circles, ...gateways.map((gateway) => this.toMarker(gateway))];
  });

  protected readonly fitBounds = computed<LatLngBounds>(() => {
    const gateways = this.mappableGateways();
    if (gateways.length === 0) {
      return DEFAULT_BOUNDS;
    }
    return latLngBounds(gateways.map((gateway) => latLng(gateway.latitude, gateway.longitude)));
  });

  protected onMapReady(map: LeafletMap): void {
    this.map.set(map);
  }

  protected toggleList(): void {
    this.listExpanded.update((expanded) => !expanded);
  }

  protected zoomToGateway(gateway: IotnetGateway): void {
    this.selectGateway(gateway);
    if (!hasCoordinates(gateway)) {
      return;
    }
    this.map()?.flyTo(latLng(gateway.latitude, gateway.longitude), IotnetMap.GATEWAY_ZOOM);
  }

  // On narrow screens the list and the detail overlay cannot coexist, so
  // selecting a gateway (for detail) collapses the list.
  private selectGateway(gateway: IotnetGateway): void {
    this.selectedGateway.set(gateway);
    if (isNarrowScreen()) {
      this.listExpanded.set(false);
    }
  }

  protected isSelected(gateway: IotnetGateway): boolean {
    return this.selectedGateway()?.gateway_id === gateway.gateway_id;
  }

  private toRangeCircle(gateway: IotnetGateway): Circle {
    return circle(latLng(gateway.latitude, gateway.longitude), {
      radius: GATEWAY_RANGE_METERS,
      color: GATEWAY_RANGE_COLOR,
      weight: 1,
      opacity: 0.5,
      dashArray: "4 6",
      fillColor: GATEWAY_RANGE_COLOR,
      fillOpacity: 0.12,
      interactive: false,
    });
  }

  private toMarker(gateway: IotnetGateway): Marker {
    const icon = divIcon({
      className: `gateway-marker gateway-marker--${gateway.health} gateway-marker--${isIotnetGateway(gateway) ? "outdoor" : "indoor"}`,
      iconSize: [MARKER_SIZE, MARKER_SIZE],
      iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE / 2],
      tooltipAnchor: [MARKER_SIZE / 2, 0],
    });
    const gatewayMarker = marker(latLng(gateway.latitude, gateway.longitude), { icon });

    // The tooltip hosts the shared caption component. It is created lazily on
    // hover and destroyed again on close (and on marker removal, since the
    // marker list is rebuilt whenever the filters change) to avoid leaking
    // component instances.
    let caption: ComponentRef<IotnetGatewayCaption> | null = null;
    const destroyCaption = () =>
      this.zone.run(() => {
        caption?.destroy();
        caption = null;
      });
    gatewayMarker.bindTooltip(
      () => {
        destroyCaption();
        caption = this.zone.run(() => this.createCaption(gateway));
        return caption.location.nativeElement as HTMLElement;
      },
      { className: "gateway-tooltip" },
    );
    gatewayMarker.on("tooltipclose", destroyCaption);
    gatewayMarker.on("remove", destroyCaption);

    // Leaflet listeners fire outside the Angular zone; re-enter it so change
    // detection picks up the selection.
    gatewayMarker.on("click", () => this.zone.run(() => this.selectGateway(gateway)));
    return gatewayMarker;
  }

  private createCaption(gateway: IotnetGateway): ComponentRef<IotnetGatewayCaption> {
    const ref = createComponent(IotnetGatewayCaption, { environmentInjector: this.injector });
    ref.setInput("gateway", gateway);
    ref.setInput("status", gateway.health);
    this.appRef.attachView(ref.hostView);
    // Render synchronously so Leaflet measures the final tooltip size.
    ref.changeDetectorRef.detectChanges();
    return ref;
  }
}
